import React, {
  useEffect,
  useState,
} from 'react';
import { useRouter } from 'next/router';

import { ShareIcons } from '@/core/components/ShareIcons';
import { ActionTakenComponent } from '@/core/components/ActionTakenComponent';
import { StoppedPageComponent } from '@/core/components/StoppedPageComponent';
import { MainMedia } from '@/core/components/MainMedia';
import {
  ActionCenterTypes,
  ActionResponseStatus,
  ContactInfo,
  FormSubjectMessagePair,
  MatchedTargets,
  MatchTargetsPayload,
  MessagePairTypes,
  SubmitResponse,
  TakeActionErrorsFields,
  TakeActionFormPayload,
  TakeActionFormState,
  TakeActionLandingPageProps,
  Targets,
} from '@/core/interfaces/common';

import { TakeActionFormContainer } from '@/features/NewForm/TakeActionFormContainer';

import { googleAnalyticsPageview } from '@/utils/analytics';
import {
  getAnalyticsSessionId,
  trackFormSubmit,
} from '@/utils/beacon';
import { request } from '@/utils/request';
import {
  getPrefilledContactUuid,
  handleCreateCustomFieldsParams,
  handleNumberFormat,
  scrollToTop,
  transformPrefix,
} from '@/utils/helpers';

import {
  Description,
  Footer,
  FooterLink,
  GeneralErrorMessage,
  Header,
  HeadlineDescription,
  LandingPageWrapper,
  Logo,
  LogoLink,
  ShareIconsWrapper,
  TextAreaDescription,
  TextDescription,
  Title,
  WantMoreWrapper,
  WhyTakeActionSection,
} from '@/themes/globalStyles';

import {
  FormWrapper,
  LogoWrapper,
  PickActionTypeWrapper,
  ResetForm,
  ResetFormButton,
  StyledPickActionType,
  StyledWhyTakeActionWrapper,
} from './TakeActionLandingPageV3.styles';

export interface CustomFieldErrors {
  value: Array<string>;
}

export const TakeActionLandingPageV3 = ({
  actionCenterDetails,
  contactInfo,
  nonGeocodedTargets,
}: TakeActionLandingPageProps) => {
  const router = useRouter();
  const { uuid } = router.query;

  const {
    customFields,
    emailChannel,
    facebookPixelEnabled,
    facebookPixelId,
    headlineConfig,
    isDonateButton,
    isGeocoded,
    googleAnalyticsEnabled,
    googleAnalyticsId,
    landingPageImage,
    linkDonate,
    logo,
    mode,
    optIn,
    organization,
    messagingType,
    phoneChannel,
    prefilledMessages,
    status,
    socialShareButtonEnabled,
    talkingPoints,
    thankYouMessage,
  } = actionCenterDetails;

  const {
    youtubeId,
    vimeoCode,
  } = headlineConfig;
  const {
    emailType,
    phoneType,
  } = ActionCenterTypes;
  const {
    messageRotation,
    messageMapping,
  } = MessagePairTypes;

  const [
    actionCenterType,
    setActionCenterType,
  ] = useState<ActionCenterTypes>(emailType);
  const [
    formState,
    setFormState,
  ] = useState<TakeActionFormState>({
    customFields: [],
    email: '',
    firstName: '',
    isOptInEmail: true,
    isOptInText: true,
    lastName: '',
    phoneNumber: '',
    prefix: '',
    streetAddress: '',
    subjectMessagePairs: [
      {
        body: [
          {
            isAllowEdit: false,
            name: '',
          },
        ],
        isAllowPersonalMessage: false,
        personalMessage: '',
        subject: {
          isAllowEdit: false,
          name: '',
        },
        uuid: '',
      },
    ],
    zipCode: '',
  });
  const [
    isNewContact,
    setIsNewContact,
  ] = useState(false);
  const [
    actionMode,
    setActionMode,
  ] = useState(ActionResponseStatus.live);
  const [
    isActionTaken,
    setIsActionTaken,
  ] = useState(false);
  const [
    targets,
    setTargets,
  ] = useState<Targets>([]);
  const [
    isPending,
    setIsPending,
  ] = useState(false);
  const [
    error,
    setError,
  ] = useState<TakeActionErrorsFields>({});
  const [
    previewTargets,
    setPreviewTargets,
  ] = useState<MatchedTargets>([]);
  const [
    currentMessageIndex,
    setCurrentMessageIndex,
  ] = useState<number>(0);

  useEffect(() => {
    if (!emailChannel && phoneChannel) {
      setActionCenterType(phoneType);
    }
  }, [
    emailChannel,
    phoneChannel,
    phoneType,
  ]);

  useEffect(() => {
    let subjectMessagePairs: Array<FormSubjectMessagePair> = [];

    if (prefilledMessages) {
      if (messagingType === messageMapping) {
        if (isGeocoded) {
          subjectMessagePairs = [
            {
              ...prefilledMessages[0],
              personalMessage: '',
            },
          ];
        } else {
          subjectMessagePairs = prefilledMessages.map(prefilledMessage => ({
            ...prefilledMessage,
            personalMessage: '',
          }));
        }
      } else if (messagingType === messageRotation) {
        subjectMessagePairs = [
          {
            ...prefilledMessages[Math.floor(
              Math.random() * prefilledMessages.length
            )],
            personalMessage: '',
          },
        ];
      }
    }

    const formCustomFields = customFields.map(customField => ({
      ...customField,
      value: contactInfo?.customFields.find(customFieldAnswer => customFieldAnswer.customField === customField.uuid)?.value || '',
    }));

    if (!isGeocoded && nonGeocodedTargets?.length) {
      setPreviewTargets(nonGeocodedTargets);
    }

    setFormState(prevValues => ({
      ...prevValues,
      customFields: formCustomFields,
      subjectMessagePairs,
    }));

    if (contactInfo) {
      const prefix = transformPrefix(contactInfo.prefix);

      setFormState(prevValues => ({
        ...prevValues,
        email: contactInfo.email,
        firstName: contactInfo.firstName,
        lastName: contactInfo.lastName,
        phoneNumber: handleNumberFormat(contactInfo.phoneNumber) || '',
        prefix,
        streetAddress: contactInfo.streetAddress,
        zipCode: contactInfo.zipCode ? contactInfo.zipCode.substring(0, 5) : '',
      }));

      const emptyFieldsError = Object.keys(contactInfo).reduce<TakeActionErrorsFields>(
        (errorFieldsAcc, key) => {
          if (key === 'customFields') {
            return errorFieldsAcc;
          }

          if (!contactInfo[key as keyof ContactInfo]) {
            return {
              ...errorFieldsAcc,
              [key]: ['This field may not be blank.'],
            };
          }

          return {
            ...errorFieldsAcc,
          };
        },
        {}
      );

      emptyFieldsError.customFields = formCustomFields.map(formCustomField => ({
        value: formCustomField.value ? [] : ['This field may not be blank.'],
      }));

      setError(prevValue => ({
        ...prevValue,
        ...emptyFieldsError,
      }));
    }
  }, [
    contactInfo,
    customFields,
    isGeocoded,
    prefilledMessages,
    messageMapping,
    messageRotation,
    messagingType,
  ]);

  useEffect(() => {
    if (facebookPixelId && facebookPixelEnabled) {
      import('react-facebook-pixel')
        .then(module => module.default)
        .then(ReactPixel => {
          ReactPixel.init(facebookPixelId, undefined, {
            autoConfig: true,
            debug: false,
          });
          ReactPixel.pageView(); // For tracking page view
        });
    }
  }, [
    facebookPixelId,
    facebookPixelEnabled,
  ]);

  useEffect(() => {
    if (googleAnalyticsId && googleAnalyticsEnabled) {
      googleAnalyticsPageview(googleAnalyticsId);
    }
  }, [
    googleAnalyticsId,
    googleAnalyticsEnabled,
  ]);

  const handleResetForm = () => {
    setIsNewContact(true);

    setFormState(prevValues => ({
      ...prevValues,
      customFields: prevValues.customFields.map(customField => ({
        ...customField,
        value: '',
      })),
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      prefix: '',
      streetAddress: '',
      zipCode: '',
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setIsPending(true);

    const params: TakeActionFormPayload = {
      action: actionCenterType,
      contact: {
        email: formState.email,
        firstName: formState.firstName,
        isOptInEmail: formState.isOptInEmail,
        isOptInText: formState.isOptInText,
        lastName: formState.lastName,
        phoneNumber: formState.phoneNumber,
        prefix: formState.prefix,
        streetAddress: formState.streetAddress,
        zipCode: formState.zipCode,
        ...getPrefilledContactUuid(contactInfo, formState, isNewContact),
      },
      sessionId: getAnalyticsSessionId(),
      ...(formState.customFields ?
        { customFields: handleCreateCustomFieldsParams(formState.customFields) } :
        {}),
      meta: {
        params: window.location.search,
        source: window.location.href,
        type: 'landing-v3',
      },
    };

    if (actionCenterType === emailType) {
      if (messagingType === messageMapping) {
        params.messages = formState.subjectMessagePairs.map(({
          body,
          personalMessage,
          uuid: subjectMessagePairUuid,
          subject,
        }) => {
          const message = body.map(messageData => messageData.name) || [];

          return ({
            message: [
              ...message,
              ...(personalMessage ? [personalMessage] : []),
            ].join('\n\n'),
            subject: subject.name,
            targets: previewTargets
              .filter(target => target.mappedMessage === subjectMessagePairUuid)
              .map(target => target.uuid),
            uuid: subjectMessagePairUuid,
          });
        });
      } else if (messagingType === messageRotation) {
        const message = formState.subjectMessagePairs[0] ?
          formState.subjectMessagePairs[0].body.map(
            messageData => messageData.name
          ) :
          [];

        params.message = [
          ...message,
          ...(formState.subjectMessagePairs[0]?.personalMessage ?
            [formState.subjectMessagePairs[0].personalMessage] :
            []
          ),
        ].join('\n\n');
        params.messageId = formState.subjectMessagePairs[0].uuid;
        params.subject = formState.subjectMessagePairs[0].subject.name;
      }
    }

    return request
      .post<SubmitResponse>(`/public/act/${uuid}/${actionCenterType === emailType ? 'send_message' : 'make_phone_call'}/`, params)
      .then(({ data }) => {
        setTargets(data.targets || []);

        if (data.status === 'error') {
          setError({
            submitError: data.message,
          });
        } else {
          trackFormSubmit({
            organization: organization.uuid,
            version: 'v3',
          });
        }

        if (data.status === ActionResponseStatus.test) {
          setActionMode(ActionResponseStatus.test);
        } else if (data.status === ActionResponseStatus.petition) {
          setActionMode(ActionResponseStatus.petition);
        }
        setIsActionTaken(true);

        return data;
      })
      .catch(({ response: { data = {} } = {} }) => {
        if (data?.status === 'error') {
          setError({
            generalError: data.message,
          });
        } else {
          const messages = data.messages ?
            data.messages :
            [
              {
                message: data.message,
                subject: data.subject,
              },
            ];

          if (messages[0].message || messages[0].subject) {
            setCurrentMessageIndex(0);
          }

          setError({
            ...(data.customFields ? { customFields: data.customFields } : {}),
            ...data.contact,
            messages,
          });
        }
      })
      .finally(() => {
        setIsPending(false);
        scrollToTop();
      });
  };

  const handleFetchPreviewTargets = (
    streetAddress: string | undefined,
    zipCode: string | undefined
  ) => {
    const contactStreetAddress = streetAddress || formState.streetAddress;
    const contactZipCode = zipCode || formState.zipCode;

    if (contactStreetAddress && contactZipCode && isGeocoded) {
      const params: MatchTargetsPayload = {
        streetAddress: contactStreetAddress,
        zipCode: contactZipCode,
      };

      request
        .get<MatchedTargets, MatchTargetsPayload>(`/public/act/${uuid}/match_targets/`, { params })
        .then(({ data }) => {
          setPreviewTargets(data || []);

          if (messagingType === messageMapping && data) {
            const filteredMessages = prefilledMessages?.filter(
              ({ uuid: messageUuid }) => data.some(target => target.mappedMessage === messageUuid)
            ) || [];

            setFormState(prevValues => ({
              ...prevValues,
              subjectMessagePairs: filteredMessages.map(filteredMessage => ({
                ...filteredMessage,
                personalMessage: '',
              })),
            }));
          }
        });
    }
  };

  useEffect(() => {
    if (contactInfo?.streetAddress && contactInfo?.zipCode && isGeocoded) {
      handleFetchPreviewTargets(contactInfo.streetAddress, contactInfo.zipCode);
    }
  }, []);

  if (status === 'stopped') {
    return (
      <StoppedPageComponent
        backgroundColor={headlineConfig?.backgroundColor || '#2979FF'}
        textColor={headlineConfig?.textColor || '#000'}
        title="This Action Center has been stopped."
      />
    );
  }

  const websiteLink = organization.websiteUrl?.startsWith('http://') || organization.websiteUrl?.startsWith('https://') ? organization.websiteUrl : `https://${organization.websiteUrl}`;

  return (
    <LandingPageWrapper backgroundcolor={headlineConfig?.backgroundColor || '#2979FF'}>
      <Header
        backgroundcolor={headlineConfig?.backgroundColor || '#2979FF'}
        isActionTaken={isActionTaken}
        textcolor={headlineConfig?.textColor || '#000'}
      >
        <LogoWrapper>
          <LogoLink
            aria-label={organization.websiteUrl ? `${organization.name} website` : undefined}
            href={organization.websiteUrl ? websiteLink : undefined}
            target="_blank"
            rel="noopener noreferrer"
          >
            {logo ?
              (
                <Logo
                  alt=""
                  role="presentation"
                  src={logo}
                />
              ) :
              organization.name
            }
          </LogoLink>
        </LogoWrapper>
        {isActionTaken ?
          (
            <ActionTakenComponent
              actionCenterType={actionCenterType}
              actionMode={actionMode}
              backgroundColor={headlineConfig?.backgroundColor || '#2979FF'}
              buttonColor={headlineConfig?.buttonColor || '#2979FF'}
              linkDonate={linkDonate}
              isDonateButton={isDonateButton}
              multipleMessages={formState.subjectMessagePairs.length > 1}
              socialShareButtonEnabled={socialShareButtonEnabled}
              submitError={error?.submitError}
              talkingPoints={talkingPoints}
              targets={targets}
              textColor={headlineConfig?.textColor || '#000'}
              thankYouMessage={thankYouMessage}
              uuid={uuid}
            />
          ) :
          (
            <>
              <Title>
                {headlineConfig?.headlineText}
              </Title>
              {headlineConfig?.secondHeadlineText && (
                <Description>
                  {headlineConfig.secondHeadlineText}
                </Description>
              )}
            </>
          )}
      </Header>
      {!isActionTaken && (
        <>
          <FormWrapper>
            <WhyTakeActionSection>
              <StyledWhyTakeActionWrapper>
                <MainMedia
                  vimeoCode={vimeoCode}
                  youtubeId={youtubeId}
                  mainImage={landingPageImage}
                />
                {headlineConfig?.descriptionHeadlineText && (
                  <HeadlineDescription>
                    {headlineConfig?.descriptionHeadlineText}
                  </HeadlineDescription>
                )}
                {headlineConfig?.descriptionBody ?
                  (
                    <TextDescription
                        // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: headlineConfig?.descriptionBody,
                      }}
                    />
                  ) :
                  (
                    <TextAreaDescription>{headlineConfig?.textArea}</TextAreaDescription>
                  )}
              </StyledWhyTakeActionWrapper>
            </WhyTakeActionSection>
            <section>
              {error?.generalError && (
                <GeneralErrorMessage>{error?.generalError}</GeneralErrorMessage>
              )}
              {emailChannel && phoneChannel && (
                <PickActionTypeWrapper>
                  <StyledPickActionType
                    actionCenterType={actionCenterType}
                    backgroundColor={headlineConfig?.backgroundColor || '#2979FF'}
                    setActionCenterType={setActionCenterType}
                    textColor={headlineConfig?.textColor || '#000'}
                  />
                </PickActionTypeWrapper>
              )}
              {!!contactInfo && !isNewContact && (
                <ResetFormButton onClick={handleResetForm}>
                  <span>
                    {`Not ${contactInfo?.firstName} ${contactInfo?.lastName}? `}
                  </span>
                  <ResetForm textcolor={headlineConfig?.backgroundColor || '#2979FF'}>
                    Reset Form
                  </ResetForm>
                </ResetFormButton>
              )}
              <form
                autoComplete="off"
                onSubmit={handleSubmit}
              >
                <TakeActionFormContainer
                  actionCenterType={actionCenterType}
                  backgroundColor={headlineConfig?.backgroundColor || '#2979FF'}
                  buttonColor={headlineConfig?.buttonColor || '#2979FF'}
                  currentMessageIndex={currentMessageIndex}
                  emailChannel={emailChannel}
                  handleBlurAddress={handleFetchPreviewTargets}
                  error={error}
                  formState={formState}
                  isPending={isPending}
                  mode={mode}
                  optIn={optIn}
                  phoneChannel={phoneChannel}
                  previewTargets={(
                    messagingType === messageMapping &&
                    actionCenterType === emailType
                  ) ?
                    // eslint-disable-next-line max-len
                    previewTargets.filter(({ mappedMessage }) => mappedMessage === formState.subjectMessagePairs[currentMessageIndex]?.uuid) :
                    previewTargets
                  }
                  setCurrentMessageIndex={setCurrentMessageIndex}
                  setError={setError}
                  setFormState={setFormState}
                  talkingPoints={talkingPoints}
                  textColor={headlineConfig?.textColor || '#000'}
                />
              </form>
            </section>
          </FormWrapper>
          <Footer
            backgroundcolor={headlineConfig?.backgroundColor || '#2979FF'}
            textcolor={headlineConfig?.textColor || '#000'}
          >
            {socialShareButtonEnabled && (
              <>
                <WantMoreWrapper>Share this page on your social media channels</WantMoreWrapper>
                <ShareIconsWrapper>
                  <ShareIcons
                    textcolor={headlineConfig?.textColor}
                    customIcons
                    uuid={uuid}
                  />
                </ShareIconsWrapper>
              </>
            )}
            <div>
              <FooterLink
                href={organization.websiteUrl ? websiteLink : undefined}
                target="_blank"
                rel="noopener noreferrer"
              >
                {organization.name}
              </FooterLink>
              &nbsp;
              |
              &nbsp;
              <FooterLink
                href="https://www.muster.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Muster&apos;s Privacy Policy
              </FooterLink>
              {organization.privacyPolicyUrl && (
                <>
                  &nbsp;
                  |
                  &nbsp;
                  <FooterLink
                    href={organization.privacyPolicyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {organization.name}
                    &apos;s Privacy Policy
                  </FooterLink>
                </>
              )}
            </div>
          </Footer>
        </>
      )}
    </LandingPageWrapper>
  );
};
